import type { CatalogColor } from "./api/types";

// ---------------------------------------------------------------------------
// Two-tone (Base + Face) colour derivation — Raynor RockCreeke-style models.
//
// The catalogue stores ONE pre-combined colour row per base × face pairing:
// Family = "Two-Tone", Name = "White / Bronzetone" (base name, " / ", face
// name). Single colours of the same models sit in the same list with plain
// names. The Base/Overlay sub-picker is purely presentational — picking a
// base + a face resolves back to one of these pre-combined rows, so the
// selection stays one colorId → one VisualKey and pricing/rendering/lead
// submission are untouched. Everything here derives from the colour list the
// SPA already receives (family + name) — no API change.
// ---------------------------------------------------------------------------

const SEPARATOR = " / ";

// Normalized match key — colour names come from the catalogue verbatim, so match
// case-insensitively (mirrors the placement normalization in DoorPicker).
export const colorNameKey = (s: string) => s.trim().toLowerCase();

export interface TwoToneParts {
  base: string;
  face: string;
}

// "White / Bronzetone" → { base: "White", face: "Bronzetone" }. The first separator wins;
// a name without one (or with an empty side) is not a combined name.
export function splitTwoToneName(name: string): TwoToneParts | null {
  const i = name.indexOf(SEPARATOR);
  if (i < 0) return null;
  const base = name.slice(0, i).trim();
  const face = name.slice(i + SEPARATOR.length).trim();
  return base && face ? { base, face } : null;
}

// A pre-combined base × face row. Family is the authoritative marker; the name must also
// split cleanly so a malformed "Two-Tone" row can never break the sub-picker.
export const isTwoToneCombo = (c: CatalogColor): boolean =>
  colorNameKey(c.family) === "two-tone" && splitTwoToneName(c.name) !== null;

// Collections with ANY combo row get the Base/Overlay sub-picker; everything else
// (Amarr, CHI, single-colour Raynor models) keeps the flat grid unchanged.
export const hasTwoToneColors = (colors: CatalogColor[]): boolean => colors.some(isTwoToneCombo);

export interface TwoToneSwatch {
  name: string; // display casing from the catalogue
  key: string; // normalized match key (colorNameKey)
  imageUrl: string | null;
}

export interface TwoToneModel {
  bases: TwoToneSwatch[];
  faces: TwoToneSwatch[];
}

// Distinct base/overlay names in first-appearance (catalogue sort) order. Single-colour rows
// join BOTH rows — they're selected as base === face. Swatch art prefers the colour's own
// single-colour row when one exists, falling back to the first combined row that uses it.
export function buildTwoToneModel(colors: CatalogColor[]): TwoToneModel {
  const bases = new Map<string, { swatch: TwoToneSwatch; own: boolean }>();
  const faces = new Map<string, { swatch: TwoToneSwatch; own: boolean }>();
  const add = (m: typeof bases, name: string, imageUrl: string | null, own: boolean) => {
    const key = colorNameKey(name);
    const cur = m.get(key);
    if (!cur) {
      m.set(key, { swatch: { name: name.trim(), key, imageUrl }, own });
    } else if (own && !cur.own) {
      // The colour's own single-colour swatch beats a combined row's art.
      cur.swatch.imageUrl = imageUrl ?? cur.swatch.imageUrl;
      cur.own = true;
    }
  };
  for (const c of colors) {
    const parts = isTwoToneCombo(c) ? splitTwoToneName(c.name) : null;
    if (parts) {
      add(bases, parts.base, c.imageUrl, false);
      add(faces, parts.face, c.imageUrl, false);
    } else {
      add(bases, c.name, c.imageUrl, true);
      add(faces, c.name, c.imageUrl, true);
    }
  }
  return {
    bases: [...bases.values()].map((v) => v.swatch),
    faces: [...faces.values()].map((v) => v.swatch),
  };
}

// base + face → the pre-combined row named "base / face"; base === face → the single-colour
// row with that name. Undefined when the catalogue has no such pairing — availability is always
// computed from the actual colour list, never fabricated.
export function resolveTwoTone(colors: CatalogColor[], base: string, face: string): CatalogColor | undefined {
  const baseKey = colorNameKey(base);
  const faceKey = colorNameKey(face);
  for (const c of colors) {
    const parts = isTwoToneCombo(c) ? splitTwoToneName(c.name) : null;
    if (parts && colorNameKey(parts.base) === baseKey && colorNameKey(parts.face) === faceKey) return c;
  }
  if (baseKey === faceKey) return colors.find((c) => !isTwoToneCombo(c) && colorNameKey(c.name) === baseKey);
  return undefined;
}

export const isComboAvailable = (colors: CatalogColor[], base: string, face: string): boolean =>
  resolveTwoTone(colors, base, face) !== undefined;

// Base/face implied by the currently selected colour row: combos split on the separator,
// single colours read as base === face (a solid door), nothing selected reads as null.
export function selectedTwoToneParts(color: CatalogColor | undefined): TwoToneParts | null {
  if (!color) return null;
  if (isTwoToneCombo(color)) return splitTwoToneName(color.name);
  const name = color.name.trim();
  return name ? { base: name, face: name } : null;
}
